import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { FixedSizeList as List } from "react-window";
import { useCurrency } from "./CurrencyContext";

// Custom MenuList using react-window
const heightPerItem = 45;
const customMenuList = (props) => {
    const { options, children, maxHeight, getValue } = props;
    const [value] = getValue();
    const initialOffset = options.indexOf(value) * heightPerItem;

    return (
        <List
            height={Math.min(maxHeight, options.length * heightPerItem)}
            itemCount={children.length}
            itemSize={heightPerItem}
            initialScrollOffset={initialOffset}
            width="100%"
        >
            {({ index, style }) => (
                <div style={style}>{children[index]}</div>
            )}
        </List>
    );
};

const customStyles = {
    control: (provided, state) => ({
        ...provided,
        borderColor: state.isFocused ? 'orangered' : "orangered",
        boxShadow: state.isFocused ? "0 0 0 1px orangered" : "none",
        "&:hover": {
            borderColor: "orangered",
        },
        fontSize: "14px",
        backgroundColor:"#FFF7ED",

    }),
    option: (provided) => ({
        ...provided,
        fontSize: "12px",
        paddingTop: 10,
        paddingBottom: 10,
    })
};

export default function CountryCurrencyDropdown() {
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { setCurrency, currency } = useCurrency();
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
        const fetchCountries = async () => {
            setIsLoading(true);

            const cacheKey = "countryOptions";
            const cacheExpiryKey = "countryOptionsExpiry";
            const oneDay = 24 * 60 * 60 * 1000;

            const cachedData = localStorage.getItem(cacheKey);
            const cachedExpiry = localStorage.getItem(cacheExpiryKey);

            if (cachedData && cachedExpiry && new Date().getTime() < Number(cachedExpiry)) {
                const parsedData = JSON.parse(cachedData);
                setOptions(parsedData);

                const defaultOption = parsedData.find(option => option.currencyCode === currency);
                if (defaultOption) setSelectedOption(defaultOption);

                setIsLoading(false);
                return;
            }

            try {
                const res = await axios.get("https://restcountries.com/v3.1/all?fields=name,currencies,cca2,flags");

                const countryOptions = res.data
                    .filter(country => country.currencies)
                    .map((country) => {
                        const currencyCode = Object.keys(country.currencies)[0];
                        const currencyInfo = country.currencies[currencyCode];

                        if (!currencyInfo || !country.flags?.png) return null;

                        return {
                            value: country.cca2,
                            label: country.name.common,
                            flag: country.flags.png,
                            currency: `${currencyInfo.symbol || currencyCode} - ${currencyInfo.name}`,
                            currencyCode: currencyCode,
                        };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.label.localeCompare(b.label)); // optional sorting

                setOptions(countryOptions);

                localStorage.setItem(cacheKey, JSON.stringify(countryOptions));
                localStorage.setItem(cacheExpiryKey, new Date().getTime() + oneDay);

                const defaultOption = countryOptions.find(option => option.currencyCode === currency);
                if (defaultOption) setSelectedOption(defaultOption);

            } catch (error) {
                console.error("Error fetching countries:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCountries();
    }, [currency]);

    const handleChange = (selected) => {
        if (!selected) return;

        setSelectedOption(selected);
        setCurrency(selected.currencyCode);
    };

    return (
        <div className="w-full max-w-xs">
            <Select
                options={options}
                value={selectedOption}
                onChange={handleChange}
                isLoading={isLoading}
                isSearchable={true}
                placeholder="Select Country Currency"
                styles={customStyles}
                components={{ MenuList: customMenuList }}
                getOptionLabel={(option) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, marginBottom: 10,}}>
                        <img src={option.flag} alt={`${option.label} flag`} width="15" />
                        <span style={{ fontSize: "9px" }}>
                            {option.label} ({option.currency})
                        </span>
                    </div>
                )}
                getOptionValue={(option) => option.value}
            />
        </div>
    );
}
